import React, { useState } from 'react';
import { ArrowLeft, Filter, User, Building, Briefcase, MapPin, Calendar, Plus, Users } from 'lucide-react';
import { EmptyState } from './EmptyState';
import { GuardRosterModal } from './GuardRosterModal';
interface Guard {
  id: string;
  name: string;
  currentShifts: number;
  grade: string;
  site: string;
  area: string;
}
interface UnderUtilisedGuardsListProps {
  onBack: () => void;
}
const guards: Guard[] = [{
  id: '1',
  name: 'John Mkhize',
  currentShifts: 14,
  grade: 'Grade B',
  site: 'Reliever Pool',
  area: 'Cape Town CBD'
}, {
  id: '2',
  name: 'Lindiwe Zulu',
  currentShifts: 12,
  grade: 'Grade C',
  site: 'Shoprite Bellville',
  area: 'Northern Suburbs'
}, {
  id: '3',
  name: 'David Botha',
  currentShifts: 16,
  grade: 'Grade A',
  site: 'FNB Foreshore',
  area: 'Cape Town CBD'
}, {
  id: '4',
  name: 'Grace Ndlovu',
  currentShifts: 10,
  grade: 'Grade B',
  site: 'Checkers Claremont',
  area: 'Southern Suburbs'
}, {
  id: '5',
  name: 'Peter van Wyk',
  currentShifts: 15,
  grade: 'Grade A',
  site: 'Woolworths Canal Walk',
  area: 'Northern Suburbs'
}, {
  id: '6',
  name: 'Themba Nkosi',
  currentShifts: 11,
  grade: 'Grade C',
  site: 'Pick n Pay Tokai',
  area: 'Southern Suburbs'
}, {
  id: '7',
  name: 'Sarah Jacobs',
  currentShifts: 13,
  grade: 'Grade B',
  site: 'Spar Sea Point',
  area: 'Cape Town CBD'
}, {
  id: '8',
  name: 'Michael Dube',
  currentShifts: 17,
  grade: 'Grade A',
  site: 'Game Parow',
  area: 'Northern Suburbs'
}];
const areas = ['All Areas', 'Cape Town CBD', 'Northern Suburbs', 'Southern Suburbs'];
export function UnderUtilisedGuardsList({
  onBack
}: UnderUtilisedGuardsListProps) {
  const [selectedArea, setSelectedArea] = useState<string>('All Areas');
  const [selectedGuard, setSelectedGuard] = useState<Guard | null>(null);
  const filteredGuards = selectedArea === 'All Areas' ? guards : guards.filter(guard => guard.area === selectedArea);
  const getAvailableShifts = (currentShifts: number) => 19 - currentShifts;
  const getCapacityColor = (available: number) => {
    if (available >= 7) return 'text-sky-600 bg-sky-500/10';
    if (available >= 4) return 'text-sky-500 bg-sky-500/10';
    return 'text-[#5a7280] bg-[#062133]/5';
  };
  const handleViewRoster = (guard: Guard) => {
    setSelectedGuard(guard);
  };
  const handleCloseRoster = () => {
    setSelectedGuard(null);
  };
  return <>
      <div className="flex flex-col space-y-6 animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-1148">
        {/* Header Section */}
        <div className="flex flex-col space-y-4" data-id="element-1149">
          <button onClick={onBack} className="group flex w-fit items-center text-sm font-medium text-[#5a7280] hover:text-[#062133] transition-colors min-h-[44px] px-2 -ml-2 rounded-lg hover:bg-[#062133]/5" aria-label="Back to dashboard" data-id="element-1150">
            <ArrowLeft className="mr-1 h-4 w-4 transition-transform group-hover:-translate-x-1" data-id="element-1151" />
            Back to Dashboard
          </button>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-1152">
            <div data-id="element-1153">
              <h2 className="text-2xl font-bold text-[#062133]" data-id="element-1154">
                Under-Utilised Guards ({guards.length})
              </h2>
              <p className="text-[#5a7280]" data-id="element-1155">
                Guards rostered below 19 shifts who have capacity for additional
                work
              </p>
            </div>

            {/* Filter Dropdown */}
            <div className="relative" data-id="element-1156">
              <div className="flex items-center space-x-2" data-id="element-1157">
                <Filter className="h-4 w-4 text-[#5a7280]" data-id="element-1158" />
                <select value={selectedArea} onChange={e => setSelectedArea(e.target.value)} className="block w-full rounded-md border-[#e5e5e5] py-2 pl-3 pr-10 text-base focus:border-[#062133] focus:outline-none focus:ring-[#062133] sm:text-sm bg-white border shadow-sm text-[#062133] min-h-[44px]" data-id="element-1159">
                  {areas.map(area => <option key={area} value={area} data-id="element-1160">
                      {area}
                    </option>)}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block overflow-hidden rounded-xl border border-[#e5e5e5] bg-white shadow-sm" data-id="element-1161">
          <table className="min-w-full divide-y divide-[#e5e5e5]" data-id="element-1162">
            <thead className="bg-[#fafaf9]" data-id="element-1163">
              <tr data-id="element-1164">
                <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-1165">
                  Guard Name
                </th>
                <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-1166">
                  Current Shifts
                </th>
                <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-1167">
                  Available Shifts
                </th>
                <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-1168">
                  Grade
                </th>
                <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-1169">
                  Primary Site
                </th>
                <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-[#5a7280] uppercase tracking-wider" data-id="element-1170">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#e5e5e5]" data-id="element-1171">
              {filteredGuards.map(guard => {
              const available = getAvailableShifts(guard.currentShifts);
              const capacityColor = getCapacityColor(available);
              return <tr key={guard.id} className="hover:bg-[#fafaf9] transition-colors cursor-pointer" onClick={() => handleViewRoster(guard)} data-id="element-1172">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1173">
                      <div className="flex items-center" data-id="element-1174">
                        <div className="h-8 w-8 rounded-full bg-[#062133]/5 flex items-center justify-center mr-3" data-id="element-1175">
                          <User className="h-4 w-4 text-[#062133]" data-id="element-1176" />
                        </div>
                        <div className="text-sm font-semibold text-[#062133]" data-id="element-1177">
                          {guard.name}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1178">
                      <div className="flex items-center text-sm text-[#062133]" data-id="element-1179">
                        <Calendar className="mr-1.5 h-4 w-4 text-[#5a7280]" data-id="element-1180" />
                        {guard.currentShifts} shifts
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1181">
                      <div className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium ${capacityColor}`} data-id="element-1182">
                        <Plus className="mr-1 h-3.5 w-3.5" data-id="element-1183" />
                        {available} available
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1184">
                      <div className="flex items-center text-sm text-[#5a7280]" data-id="element-1185">
                        <Briefcase className="mr-1.5 h-4 w-4" data-id="element-1186" />
                        {guard.grade}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1187">
                      <div className="flex items-center text-sm text-[#062133]" data-id="element-1188">
                        <Building className="mr-1.5 h-4 w-4 text-[#5a7280]" data-id="element-1189" />
                        {guard.site}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1190">
                      <button onClick={e => {
                    e.stopPropagation();
                    handleViewRoster(guard);
                  }} className="inline-flex items-center px-3 py-1.5 border border-[#e5e5e5] shadow-sm text-xs font-medium rounded-md text-[#5a7280] bg-white hover:bg-[#fafaf9] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#062133] min-h-[32px]" data-id="element-1191">
                        <Calendar className="mr-1.5 h-3.5 w-3.5" data-id="element-1192" />
                        View Roster
                      </button>
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
          {filteredGuards.length === 0 && <div className="p-0 border-none" data-id="element-1193">
              <EmptyState icon={Users} title="No under-utilised guards" description="All guards are optimally scheduled" data-id="element-1194" />
            </div>}
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden space-y-4" data-id="element-1195">
          {filteredGuards.map(guard => {
          const available = getAvailableShifts(guard.currentShifts);
          const capacityColor = getCapacityColor(available);
          return <div key={guard.id} className="bg-white rounded-xl border border-[#e5e5e5] p-5 shadow-sm space-y-4 cursor-pointer hover:border-[#5a7280]/30 transition-colors" onClick={() => handleViewRoster(guard)} data-id="element-1196">
                <div className="flex justify-between items-start" data-id="element-1197">
                  <div className="flex items-center" data-id="element-1198">
                    <div className="h-10 w-10 rounded-full bg-[#062133]/5 flex items-center justify-center mr-3" data-id="element-1199">
                      <User className="h-5 w-5 text-[#062133]" data-id="element-1200" />
                    </div>
                    <div data-id="element-1201">
                      <h3 className="text-lg font-semibold text-[#062133]" data-id="element-1202">
                        {guard.name}
                      </h3>
                      <div className="flex items-center text-xs text-[#5a7280]" data-id="element-1203">
                        <Briefcase className="mr-1 h-3 w-3" data-id="element-1204" />
                        {guard.grade}
                      </div>
                    </div>
                  </div>
                  <div className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${capacityColor}`} data-id="element-1205">
                    <Plus className="mr-1 h-3 w-3" data-id="element-1206" />
                    {available} available
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 py-3 border-t border-b border-[#e5e5e5]" data-id="element-1207">
                  <div data-id="element-1208">
                    <p className="text-xs text-[#5a7280] uppercase tracking-wider mb-1" data-id="element-1209">
                      Current Shifts
                    </p>
                    <div className="flex items-center text-sm font-medium text-[#062133]" data-id="element-1210">
                      <Calendar className="mr-1.5 h-4 w-4 text-[#5a7280]" data-id="element-1211" />
                      {guard.currentShifts}
                    </div>
                  </div>
                  <div data-id="element-1212">
                    <p className="text-xs text-[#5a7280] uppercase tracking-wider mb-1" data-id="element-1213">
                      Available
                    </p>
                    <div className="flex items-center text-sm font-medium text-sky-600" data-id="element-1214">
                      <Plus className="mr-1.5 h-4 w-4" data-id="element-1215" />
                      {available} shifts
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between" data-id="element-1216">
                  <div className="space-y-2" data-id="element-1217">
                    <div data-id="element-1218">
                      <p className="text-xs text-[#5a7280] uppercase tracking-wider mb-1" data-id="element-1219">
                        Primary Site
                      </p>
                      <div className="flex items-center text-sm font-medium text-[#062133]" data-id="element-1220">
                        <Building className="mr-1.5 h-4 w-4 text-[#5a7280]" data-id="element-1221" />
                        {guard.site}
                      </div>
                    </div>
                    <div data-id="element-1222">
                      <p className="text-xs text-[#5a7280] uppercase tracking-wider mb-1" data-id="element-1223">
                        Area
                      </p>
                      <div className="flex items-center text-sm text-[#5a7280]" data-id="element-1224">
                        <MapPin className="mr-1.5 h-4 w-4" data-id="element-1225" />
                        {guard.area}
                      </div>
                    </div>
                  </div>
                  <button onClick={e => {
                e.stopPropagation();
                handleViewRoster(guard);
              }} className="inline-flex items-center px-3 py-2 border border-[#e5e5e5] shadow-sm text-xs font-medium rounded-lg text-[#5a7280] bg-white hover:bg-[#fafaf9] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#062133]" data-id="element-1226">
                    <Calendar className="mr-1.5 h-3.5 w-3.5" data-id="element-1227" />
                    View Roster
                  </button>
                </div>
              </div>;
        })}
          {filteredGuards.length === 0 && <EmptyState icon={Users} title="No under-utilised guards" description="All guards are optimally scheduled" data-id="element-1228" />}
        </div>
      </div>

      {/* Guard Roster Modal */}
      <GuardRosterModal isOpen={selectedGuard !== null} guardName={selectedGuard?.name || ''} guardId={selectedGuard?.id || ''} grade={selectedGuard?.grade || ''} primarySite={selectedGuard?.site || ''} totalShifts={selectedGuard?.currentShifts || 0} onClose={handleCloseRoster} data-id="element-1229" />
    </>;
}